import {
  Component,
  HostBinding,
  input,
  InputSignal,
  OnInit,
  Optional,
  output,
  Self,
  Signal,
  signal,
} from '@angular/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { debounceTime, switchMap } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ApplyPipe } from '../../apply.pipe';
import {
  defaultDisplayFn,
  DisplayWithSignal,
  QueryFn,
} from './lib/autocomplete';
import { NoopValueAccessorDirective } from '../noop-value-accessor.directive';
import { NgIf } from '@angular/common';

@Component({
  selector: 'app-autocomplete',
  standalone: true,
  imports: [
    MatFormField,
    MatAutocomplete,
    MatOption,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatInput,
    MatLabel,
    ApplyPipe,
    NgIf,
  ],
  hostDirectives: [NoopValueAccessorDirective],
  templateUrl: './autocomplete.component.html',
})
export class AutocompleteComponent<T> implements OnInit {
  @HostBinding('class') class = 'w-full';
  formControl = new FormControl<T | null>(null);
  inputValue = signal('');
  blur = output();
  panelWidth = input<string | number>();
  // Casting panelWidth above directly causes problems with Angular's static analysis?
  // We remove `| undefined` from the generic so that we can omit panelWidth
  // and pass undefined to inner panelWidth to leave value unset.
  _panelWidth = this.panelWidth as InputSignal<string | number>;

  // Filtered items signal
  items: Signal<T[]> = toSignal(
    toObservable(this.inputValue).pipe(
      debounceTime(250),
      switchMap((search) => {
        return this.queryFn()(search ?? '');
      }),
    ),
    { initialValue: [] },
  );

  // Rendering inputs
  label = input('');
  displayWith: DisplayWithSignal<T> = input(defaultDisplayFn, {
    transform: (fn) => (item: T) => (item ? fn(item) : ''),
  });
  queryFn = input.required<QueryFn<T>>();

  constructor(@Optional() @Self() private readonly ngControl: NgControl) {}

  ngOnInit() {
    // Retrieve the passed in ngControl, or use our own default one.
    if (this.ngControl?.control) {
      if (this.ngControl.control instanceof FormControl) {
        this.formControl = this.ngControl.control;
      } else {
        throw new Error(`Autocomplete only supports FormControl.`);
      }
    }
  }

  setInputValue($event: Event) {
    this.inputValue.set(($event.target as HTMLInputElement).value);
  }

  handleBlur() {
    this.blur.emit();
  }
}
